import { Button } from "@/view/components/ui/button";
import { Input } from "@/view/components/ui/input";
import { Spinner } from "@/view/components/ui/spinner";
import { ChartColumn, ChevronLeft, ChevronRight, Search } from "lucide-react";
import React from "react";
import { useLinksController } from "./useLinksController";
import QRCodeModal from "./components/QRCodeModal";
import CreateLinkModal from "./components/CreateLinkModal";
import { Tabs, TabsList, TabsTrigger } from "@/view/components/TabsTable";
import { LeadSortDropdown } from "./components/LeadSortDropdown";
import { LeadList } from "./components/LeadList";
import DetailsLeadModal from "./components/DetailsLeadModal";
import { PartnerImage } from "@/view/components/PartnerImage";

const Links = () => {
  const {
    data,
    isFetching,
    hasNextPage,
    isFetchingNextPage,
    handleSearchInputChange,
    ref,
    searchValue,
    isModalOpen,
    selectedLink,
    isCreateLinkModalOpen,
    newLinkName,
    selectedTables,
    setIsCreateLinkModalOpen,
    setNewLinkName,
    setSelectedTables,
    handleOpenModal,
    handleCloseModal,
    handleCreateLink,
    isPending,
    handleLinkClick,
    selectedTab,
    setSelectedTab,
    leadsData,
    isFetchingLeads,
    searchValueLead,
    handleSearchInputChangeLead,
    openWhatsApp,
    contactedLeads,
    sort,
    handleSortChange,
    sortIcon,
    renderNoLeads,
    page,
    totalPages,
    handlePreviousLeads,
    handleNextLeads,
    selectedLeadId,
    isModalDetailsOpen,
    openDetailsModal,
    closeModal,
    getPartnerTypes,
    productsCatalog,
  } = useLinksController();

  return (
    <div className="min-h-[1280px]">
      <h1 className="text-2xl font-semibold">Links e Leads</h1>
      <p className="my-5 font-medium text-black04">
        Compartilhe um link para que seus clientes simulem e solicitem o saque
        sozinhos. Você acompanha tudo por aqui e recebe sua comissão
      </p>

      <div className="w-full border-b-[1px] bg-gray01" />

      <Tabs
        value={selectedTab}
        onValueChange={(value) => setSelectedTab(value as "leads" | "links")}
        className="mt-6"
      >
        <TabsList className="flex items-center gap-8">
          <TabsTrigger value="leads" className="text-lg">
            Leads
          </TabsTrigger>
          <TabsTrigger value="links" className="text-lg">
            Links
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {selectedTab === "leads" && (
        <>
          <div className="mt-10">
            <div className="relative mt-4 flex w-full items-center">
              <Search size={20} className="z-2 absolute left-4 top-3" />
              <Input
                className="w-full rounded-lg pl-12"
                placeholder="Pesquisar"
                onChange={handleSearchInputChangeLead}
                value={searchValueLead}
              />
              <LeadSortDropdown
                sort={sort}
                sortIcon={sortIcon}
                onSortChange={handleSortChange}
              />
            </div>
            <div className="mt-5 flex flex-col gap-4">
              {!isFetchingLeads && renderNoLeads()
                ? renderNoLeads()
                : leadsData?.leads.map((lead, index) => (
                    <LeadList
                      key={lead._id}
                      lead={lead}
                      onClick={() => openDetailsModal(lead)}
                      contacted={contactedLeads.has(lead._id)}
                      onContactClick={() =>
                        openWhatsApp({
                          _id: lead._id,
                          phone: lead.phone,
                        })
                      }
                      isLast={index === leadsData.leads.length - 1}
                    />
                  ))}
            </div>

            <div>
              {isFetching && (
                <div className="mt-4 flex items-center justify-center">
                  <Spinner />
                </div>
              )}
            </div>
            <div className="mt-4 flex items-center justify-end gap-2">
              <Button
                variant="ghost"
                onClick={handlePreviousLeads}
                disabled={page === 1}
              >
                <ChevronLeft />
              </Button>
              <div className="mx-4 flex gap-2">
                {page} de {totalPages}
              </div>
              <Button
                variant="ghost"
                onClick={handleNextLeads}
                disabled={page === totalPages}
              >
                <ChevronRight />
              </Button>
            </div>
          </div>
        </>
      )}

      {selectedTab === "links" && (
        <>
          <div className="mb-5 mt-10 flex flex-col-reverse items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="w-full">
              <div className="relative flex w-full items-center">
                <Search size={20} className="z-2 absolute left-4 top-3" />
                <Input
                  className="w-full rounded-lg pl-10 sm:w-96"
                  placeholder="Pesquisar"
                  onChange={handleSearchInputChange}
                  value={searchValue}
                />
              </div>
            </div>
            <Button
              type="submit"
              className="w-full rounded-lg bg-primary text-base font-bold text-white01 sm:w-auto"
              onClick={() => setIsCreateLinkModalOpen(true)}
            >
              <i className="fa-solid fa-link mr-2" />
              Criar novo link
            </Button>
          </div>
          <div className="space-y-4">
            {data &&
              data.pages.map((page, index) => (
                <React.Fragment key={index}>
                  {page.links.map((link) => (
                    <div
                      key={link._id}
                      className="flex items-center justify-between border-b border-gray01 pb-4"
                    >
                      <div
                        className="flex flex-1 cursor-pointer flex-col items-start"
                        onClick={() => handleLinkClick(link)}
                      >
                        <p className="mb-2 text-base font-semibold text-black01">
                          {link.label ?? link.name}
                        </p>
                        <div className="mt-2 flex flex-row items-center">
                          <div className="flex flex-row items-center gap-1">
                            <ChartColumn color="#262933" size={16} />
                            <span className="ml-1 font-semibold text-black01">
                              {link.entry_count.length}
                            </span>
                          </div>
                          <div className="ml-4 flex items-center gap-2">
                            {getPartnerTypes(link.tables ?? []).map((type) => (
                              <PartnerImage
                                key={type}
                                type={type}
                                products={productsCatalog}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                      <div>
                        <Button
                          variant="ghost"
                          className="bg-white02 p-2 hover:bg-white02"
                          onClick={() => handleOpenModal(link)}
                        >
                          <i className="fa-solid fa-qrcode text-xl text-black03"></i>
                        </Button>
                      </div>
                    </div>
                  ))}
                </React.Fragment>
              ))}
          </div>
          <div className="mt-2 flex items-center justify-center" ref={ref}>
            <Button
              variant="ghost"
              disabled={!hasNextPage || isFetchingNextPage}
            >
              {isFetchingNextPage ? <Spinner /> : null}
            </Button>
          </div>
          <div>
            {isFetching && !isFetchingNextPage ? (
              <div className="flex items-center justify-center">
                <Spinner />
              </div>
            ) : null}
          </div>
        </>
      )}

      <QRCodeModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        linkName={selectedLink.name}
      />

      <CreateLinkModal
        visible={isCreateLinkModalOpen}
        newLinkName={newLinkName}
        setNewLinkName={setNewLinkName}
        selectedTables={selectedTables}
        setSelectedTables={setSelectedTables}
        onCreateLink={handleCreateLink}
        isLoading={isPending}
        onClose={() => setIsCreateLinkModalOpen(false)}
      />

      <DetailsLeadModal
        visible={isModalDetailsOpen}
        onClose={closeModal}
        leadId={selectedLeadId}
        onContact={(lead) => {
          openWhatsApp({
            _id: lead._id,
            phone: lead.phone,
          });
        }}
      />
    </div>
  );
};

export default Links;
